// alert-message.service.ts
import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../../environments/environment';

type SwalLikeResult<T = any> = {
  isConfirmed: boolean;
  isDenied: boolean;
  isDismissed: boolean;
  value: T | null;
  dismiss?: any;
};

@Injectable({ providedIn: 'root' })
export class AlertMessageService {
  private renderer: Renderer2;
  private modalEl?: HTMLElement;
  private messageEl?: HTMLElement;
  private titleEl?: HTMLElement;
  private btnYesEl?: HTMLButtonElement;
  private btnNoEl?: HTMLButtonElement;
  private backdropClickToDismiss = false; // cámbialo a true si quieres cerrar al hacer click fuera

  // Para evitar dobles clicks o aperturas simultáneas
  private isOpen = false;

  constructor(
    private translate: TranslateService,
    private router: Router,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  // -------------------------
  // SweetAlert2 "session off"
  // -------------------------
  public showSessionOff(): Promise<SwalLikeResult<boolean>> {
    // Reutilizamos showMessage con botón OK
    return this.showMessage(
      this.translate.instant('GENERAL.ON_SESSION_EXPIRED_TITLE'),
      this.translate.instant('GENERAL.ON_SESSION_EXPIRED_MESSAGE'),
      'error',
      this.translate.instant('GENERAL.OK')
    ).then((res) => {
      if (res.isConfirmed) {
        this.router.navigate(['/login']);
      }
      return res;
    });
  }

  // -------------------------
  // Confirmación estilo Swal
  // -------------------------
  public showQuestion(
    title: string,
    message: string,
    btnOk: string | null = null,
    btnCancel: string | null = null
  ): Promise<SwalLikeResult<boolean>> {
    this.ensureModal();
    if (!this.modalEl || !this.messageEl || !this.btnYesEl || !this.btnNoEl) {
      // Si por algo no se pudo crear, devolvemos dismiss
      return Promise.resolve({
        isConfirmed: false,
        isDenied: false,
        isDismissed: true,
        value: null,
        dismiss: 'modal-missing'
      });
    }

    // Título (opcional)
    if (this.titleEl) {
      this.titleEl.textContent = title || '';
      this.titleEl.style.display = title ? '' : 'none';
    }

    // Mensaje y textos de botones
    this.messageEl.textContent = message || '';
    this.btnYesEl.textContent = btnOk || this.translate.instant('GENERAL.YES');
    this.btnNoEl.textContent = btnCancel || this.translate.instant('GENERAL.NO');
    const iconEl = this.modalEl.querySelector('.confirmation-icon') as HTMLElement | null;
    if (iconEl) {
      iconEl.textContent = '?';
    }

    // Mostrar modal
    this.openModal();

    return new Promise<SwalLikeResult<boolean>>((resolve) => {
      const onYes = () => {
        cleanup();
        resolve({isConfirmed: true, isDenied: false, isDismissed: false, value: true});
      };
      const onNo = () => {
        cleanup();
        resolve({isConfirmed: false, isDenied: false, isDismissed: true, value: false, dismiss: 'cancel'});
      };
      const onBackdrop = (e: MouseEvent) => {
        if (!this.backdropClickToDismiss) return;
        if (e.target === this.modalEl) {
          cleanup();
          resolve({isConfirmed: false, isDenied: false, isDismissed: true, value: null, dismiss: 'backdrop'});
        }
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          cleanup();
          resolve({isConfirmed: false, isDenied: false, isDismissed: true, value: null, dismiss: 'esc'});
        }
      };

      this.btnYesEl!.addEventListener('click', onYes, {once: true});
      this.btnNoEl!.addEventListener('click', onNo, {once: true});
      this.modalEl!.addEventListener('click', onBackdrop);
      this.document.addEventListener('keydown', onKey);

      const cleanup = () => {
        this.btnYesEl!.removeEventListener('click', onYes);
        this.btnNoEl!.removeEventListener('click', onNo);
        this.modalEl!.removeEventListener('click', onBackdrop);
        this.document.removeEventListener('keydown', onKey);
        this.closeModal();
      };
    });
  }

  // -------------------------
  // Mensaje OK estilo Swal
  // -------------------------
  public showMessage(
    title: string | null,
    message: string,
    typeMessage: 'info' | 'success' | 'warning' | 'error' = 'info',
    buttonOk: string | null = null
  ): Promise<SwalLikeResult<boolean>> {
    this.ensureModal();
    if (!this.modalEl || !this.messageEl || !this.btnYesEl || !this.btnNoEl) {
      return Promise.resolve({
        isConfirmed: false,
        isDenied: false,
        isDismissed: true,
        value: null,
        dismiss: 'modal-missing'
      });
    }

    // Configuramos título/mensaje
    if (this.titleEl) {
      this.titleEl.textContent = title || '';
      this.titleEl.style.display = title ? '' : 'none';
    }
    this.messageEl.innerHTML = message || '';

    // Ícono simple según tipo (puedes mejorarlo con SVG)
    const iconEl = this.modalEl.querySelector('.confirmation-icon') as HTMLElement | null;
    if (iconEl) {
      const map: Record<string, string> = {
        info: 'ℹ️',
        success: '✅',
        warning: '⚠️',
        error: '❌',
        question: '❓'
      };
      iconEl.textContent = map[typeMessage] || 'ℹ️';
    }

    // Mostramos sólo botón OK
    this.btnYesEl.textContent = buttonOk || this.translate.instant('GENERAL.OK');
    this.btnNoEl.style.display = 'none';

    this.openModal();

    return new Promise<SwalLikeResult<boolean>>((resolve) => {
      const onYes = () => {
        cleanup();
        resolve({isConfirmed: true, isDenied: false, isDismissed: false, value: true});
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          cleanup();
          resolve({isConfirmed: false, isDenied: false, isDismissed: true, value: null, dismiss: 'esc'});
        }
      };

      this.btnYesEl!.addEventListener('click', onYes, {once: true});
      this.document.addEventListener('keydown', onKey);

      const cleanup = () => {
        this.btnYesEl!.removeEventListener('click', onYes);
        this.document.removeEventListener('keydown', onKey);
        if (this.btnNoEl) this.btnNoEl.style.display = 'none';

        if (this.btnNoEl) this.btnNoEl.style.display = ''; // restaurar de forma segura
        this.closeModal();
      };
    });
  }

  public showMessageError(message: string, title?: string) {
    return this.showMessage(
      title ?? this.translate.instant('GENERAL.WARNING'),
      message,
      'error'
    );
  }

  public showErrorWhenSave() {
    return this.showMessage(null, this.translate.instant('GENERAL.ERRORS.AN_ERROR_WHEN_SAVED'), 'error');
  }

  public showSuccessSave() {
    return this.showMessage(null, this.translate.instant('GENERAL.ON_REGISTERED_SUCCESSFULLY'), 'success');
  }

  public showLimitLicenseUser() {
    return this.showMessage(
      this.translate.instant('USERS.ERRORS.LIMIT_USER_REPRESENTATIVE'),
      this.translate.instant('USERS.ERRORS.LIMIT_USER_REPRESENTATIVE_MESSAGE'),
      'warning'
    );
  }

  public showLockWhenDelete() {
    return this.showMessage(
      this.translate.instant('GENERAL.WARNING'),
      this.translate.instant('GENERAL.AN_LOCK_WHEN_DELETED'),
      'warning'
    );
  }

  public showCompanySelected() {
    return this.showMessage(
      this.translate.instant('GENERAL.WARNING'),
      this.translate.instant('COMPANIES.ERRORS.COMPANY_SELECTED'),
      'warning'
    );
  }

  // ============ Helpers ============

  private ensureModal(): void {
    if (this.modalEl) return;

    // Inyecta estilos una sola vez
    const styleId = 'confirmation-modal-styles';
    if (!this.document.getElementById(styleId)) {
      const style = this.renderer.createElement('style');
      style.id = styleId;
      style.textContent = `
      .confirmation-modal{display:none;position:fixed;top:0;left:0;width:100%;height:100%;
        background-color:rgba(0,0,0,.5);z-index:3000;backdrop-filter:blur(4px)}
      .confirmation-modal.show{display:flex;align-items:center;justify-content:center}
      .confirmation-modal-content{background:#fff;border-radius:8px;padding:24px 24px 20px;text-align:center;max-width:420px;width:calc(100% - 24px);
        box-shadow:0 10px 30px rgba(0,0,0,.3)}
      .confirmation-title{font-size:18px;font-weight:400;margin:0 0 8px;color:#111}
      .confirmation-icon{width:60px;height:60px;border-radius:50%;background:#e3f2fd;color:#1976d2;font-size:30px;
        font-weight:700;display:flex;align-items:center;justify-content:center;margin:0 auto 12px}
      .confirmation-text{font-size:16px;color:#333;margin-bottom:18px;line-height:1.45}
      .confirmation-buttons{display:flex;gap:12px;justify-content:center}
      .confirmation-btn-yes{background:${environment.primaryColor || '#1976d2'};color:#fff;border:none;padding:10px 24px;border-radius:4px;cursor:pointer;font-size:14px;font-weight:500;transition:background-color .2s}
      .confirmation-btn-yes:hover{filter:brightness(.95)}
      .confirmation-btn-no{background:#f5f5f5;color:#333;border:1px solid #e0e0e0;padding:10px 24px;border-radius:4px;cursor:pointer;font-size:14px;font-weight:500;transition:all .2s}
      .confirmation-btn-no:hover{background:#e0e0e0}
      `;
      this.renderer.appendChild(this.document.head, style);
    }

    // Crea el modal en <body> (una sola vez)
    const modal = this.renderer.createElement('div');
    this.renderer.addClass(modal, 'confirmation-modal');

    const content = this.renderer.createElement('div');
    this.renderer.addClass(content, 'confirmation-modal-content');

    const title = this.renderer.createElement('h3');
    this.renderer.addClass(title, 'confirmation-title');

    const icon = this.renderer.createElement('div');
    this.renderer.addClass(icon, 'confirmation-icon');
    icon.textContent = '?';

    const text = this.renderer.createElement('div');
    this.renderer.addClass(text, 'confirmation-text');

    const btns = this.renderer.createElement('div');
    this.renderer.addClass(btns, 'confirmation-buttons');

    const yesBtn = this.renderer.createElement('button');
    this.renderer.addClass(yesBtn, 'confirmation-btn-yes');

    const noBtn = this.renderer.createElement('button');
    this.renderer.addClass(noBtn, 'confirmation-btn-no');

    this.renderer.appendChild(btns, yesBtn);
    this.renderer.appendChild(btns, noBtn);

    this.renderer.appendChild(content, icon);
    this.renderer.appendChild(content, title);
    this.renderer.appendChild(content, text);
    this.renderer.appendChild(content, btns);
    this.renderer.appendChild(modal, content);
    this.renderer.appendChild(this.document.body, modal);

    // Guarda referencias
    this.modalEl = modal as HTMLElement;
    this.titleEl = title as HTMLElement;
    this.messageEl = text as HTMLElement;
    this.btnYesEl = yesBtn as HTMLButtonElement;
    this.btnNoEl = noBtn as HTMLButtonElement;
  }

  private openModal(): void {
    if (!this.modalEl || this.isOpen) return;
    this.isOpen = true;
    this.modalEl.classList.add('show');
    // Previene scroll del body
    this.document.body.style.overflow = 'hidden';
  }

  private closeModal(): void {
    if (!this.modalEl) return;
    this.isOpen = false;
    this.modalEl.classList.remove('show');
    this.document.body.style.overflow = '';
  }

  private ensureToastStyles(): void {
    const styleId = 'app-toast-styles';
    if (this.document.getElementById(styleId)) return;

    const style = this.renderer.createElement('style');
    style.id = styleId;
    style.textContent = `
    /* Contenedor fijo arriba-derecha, apila los toasts */
    #app-toast-container{
      position:fixed; top:20px; right:20px; z-index:4000;
      display:flex; flex-direction:column; gap:10px;
      pointer-events:none; /* no bloquea clicks del UI debajo */
    }

    /* Base del toast (mismo look & feel que notification) */
    .app-toast{
      min-width:240px; max-width:380px;
      padding:10px 14px; border-radius:8px; color:#fff;
      box-shadow:0 8px 24px rgba(0,0,0,.15);
      font-size:14px; line-height:1.35; font-weight:500;
      opacity:0; transform:translateX(30%);
      pointer-events:auto; /* permite hover si quieres */
      will-change: transform, opacity;
      animation: toastIn .25s ease-out forwards;
    }

    .app-toast-success{ background:#4CAF50; }
    .app-toast-error  { background:#F44336; }
    .app-toast-info   { background:#2196F3; }
    .app-toast-warning{ background:#FF9800; }

    @keyframes toastIn {
      from { transform: translateX(30%); opacity:0; }
      to   { transform: translateX(0);   opacity:1; }
    }

    @keyframes toastOut {
      from { opacity:1; }
      to   { opacity:0; }
    }
  `;
    this.renderer.appendChild(this.document.head, style);
  }

  public showToast(
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'success',
    duration = 3000
  ): void {
    this.ensureToastStyles();

    // 1) Contenedor (una sola vez)
    const containerId = 'app-toast-container';
    let container = this.document.getElementById(containerId);
    if (!container) {
      container = this.renderer.createElement('div') as HTMLElement;
      container.id = containerId;
      this.renderer.appendChild(this.document.body, container);
    }

    // 2) Crea el toast
    const toast = this.renderer.createElement('div');
    this.renderer.addClass(toast, 'app-toast');
    this.renderer.addClass(toast, `app-toast-${type}`);
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

    // contenido
    toast.textContent = message;

    // 3) Agrega al container
    this.renderer.appendChild(container, toast);

    // 4) Programar salida y eliminación
    const exitMs = 250; // duración de la animación de salida
    const removeAt = Math.max(0, duration - exitMs);

    const fadeTimer = setTimeout(() => {
      // Animación de salida
      (toast as HTMLElement).style.animation = `toastOut ${exitMs}ms linear forwards`;
    }, removeAt);

    const removeTimer = setTimeout(() => {
      try {
        container?.removeChild(toast);
      } catch { /* no-op */ }
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    }, duration);
  }
}
